import { useCoreServices } from '@bangle.io/context';
import {
  type ExportProgress,
  exportWorkspaceAsZip,
  type ImportProgress,
  importFilesToWorkspace,
  isFolderTreeFile,
  isImportableFile,
} from '@bangle.io/service-core';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsPage,
  toast,
} from '@bangle.io/ui-components';
import { useAtomValue } from 'jotai';
import { Download, FolderOpen, Upload } from 'lucide-react';
import React from 'react';

export function ImportExportSettingsPage() {
  const { commandDispatcher, fileSystem, workspaceState } = useCoreServices();
  const currentWsName = useAtomValue(workspaceState.$currentWsName);
  const workspaces = useAtomValue(workspaceState.$workspaces);
  const [busy, setBusy] = React.useState<'export' | 'import' | undefined>();
  const [importProgress, setImportProgress] = React.useState<
    ImportProgress | undefined
  >();
  const [exportProgress, setExportProgress] = React.useState<
    ExportProgress | undefined
  >();
  const [selectedWsName, setSelectedWsName] = React.useState<
    string | undefined
  >();

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const folderInputRef = React.useRef<HTMLInputElement>(null);

  // `$workspaces` returns a fresh array on every recompute; key on names so the
  // selected target is not reset by unrelated list identity churn.
  const workspaceNamesKey = workspaces
    .map((workspace) => workspace.name)
    .join('\0');
  const workspaceNames = React.useMemo(
    () => (workspaceNamesKey ? workspaceNamesKey.split('\0') : []),
    [workspaceNamesKey],
  );

  React.useEffect(() => {
    setSelectedWsName((previous) => {
      if (previous && workspaceNames.includes(previous)) {
        return previous;
      }
      if (currentWsName && workspaceNames.includes(currentWsName)) {
        return currentWsName;
      }
      return workspaceNames[0];
    });
  }, [currentWsName, workspaceNames]);

  const targetWsName = selectedWsName;
  const hasWorkspace = Boolean(targetWsName);
  const workspaceItems: Record<string, string> = Object.fromEntries(
    workspaceNames.map((name) => [name, name]),
  );
  const importPercent =
    importProgress && importProgress.total > 0
      ? Math.min(
          100,
          Math.round((importProgress.completed / importProgress.total) * 100),
        )
      : 0;
  const exportPercent =
    exportProgress && exportProgress.total > 0
      ? Math.min(
          100,
          Math.round((exportProgress.completed / exportProgress.total) * 100),
        )
      : 0;

  const runImport = async (fileList: FileList | null) => {
    if (!targetWsName || !fileList || fileList.length === 0) {
      return;
    }

    setBusy('import');
    setImportProgress({ completed: 0, total: fileList.length });
    // Let React paint the progress panel before work starts — otherwise a fast
    // import finishes in the same turn and the UI never appears.
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => resolve());
      });
    });

    try {
      const result = await importFilesToWorkspace({
        wsName: targetWsName,
        files: Array.from(fileList),
        fileSystem,
        onProgress: setImportProgress,
      });
      if (result.imported === 0) {
        toast.error(t.app.toasts.workspaceImportEmpty);
        return;
      }
      setImportProgress((previous) =>
        previous
          ? {
              ...previous,
              completed: previous.total,
            }
          : previous,
      );
      toast.success(
        t.app.toasts.workspaceImported({
          imported: result.imported,
          renamed: result.renamed,
          skipped: result.skipped,
          wsName: targetWsName,
        }),
      );
      commandDispatcher.dispatch('command::ws:refresh-file-tree', null, 'ui');
      // Hold the completed bar briefly so short imports are still visible.
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 700);
      });
    } catch {
      toast.error(t.app.toasts.workspaceImportFailed);
    } finally {
      setBusy(undefined);
      setImportProgress(undefined);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (folderInputRef.current) {
        folderInputRef.current.value = '';
      }
    }
  };

  const runExport = async () => {
    if (!targetWsName) {
      return;
    }
    setBusy('export');
    setExportProgress({ completed: 0, total: 1 });
    // Let React paint the progress panel before work starts — otherwise a fast
    // export finishes in the same turn and the UI never appears.
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => resolve());
      });
    });

    try {
      const result = await exportWorkspaceAsZip({
        wsName: targetWsName,
        fileSystem,
        onProgress: setExportProgress,
      });
      setExportProgress((previous) =>
        previous
          ? {
              ...previous,
              completed: previous.total,
            }
          : previous,
      );
      toast.success(
        t.app.toasts.workspaceExported({
          fileCount: result.fileCount,
          fileName: result.filename,
        }),
      );
      // Hold the completed bar briefly so short exports are still visible.
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 700);
      });
    } catch {
      toast.error(t.app.toasts.workspaceExportFailed);
    } finally {
      setBusy(undefined);
      setExportProgress(undefined);
    }
  };

  return (
    <div className="flex flex-col gap-9">
      {!hasWorkspace || !targetWsName ? (
        <p
          className="text-muted-foreground text-sm"
          data-testid="settings-import-export-no-workspace"
        >
          {t.app.settings.importExport.noWorkspace}
        </p>
      ) : (
        <SettingsPage.SettingsSection
          title={t.app.settings.importExport.targetSection}
        >
          <SettingsPage.SettingsRow
            control={
              <Select
                items={workspaceItems}
                onValueChange={(value) => {
                  if (value) {
                    setSelectedWsName(value);
                  }
                }}
                value={targetWsName}
              >
                <SelectTrigger
                  aria-label={t.app.settings.importExport.targetWorkspaceLabel}
                  className="w-full sm:w-56"
                  data-testid="settings-import-export-workspace"
                  disabled={busy !== undefined}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end" className="min-w-44">
                  {workspaceNames.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
            description={t.app.settings.importExport.targetDescription}
            title={t.app.settings.importExport.targetTitle}
          />
        </SettingsPage.SettingsSection>
      )}

      <input
        accept=".md,.markdown,.zip,text/markdown,application/zip"
        className="hidden"
        data-testid="settings-import-files-input"
        multiple
        onChange={(event) => {
          const files = event.target.files;
          if (!files?.length) {
            return;
          }
          // Folder picks include nested paths via webkitRelativePath. Flat
          // picks should be markdown/zip only.
          const isFolderPick = Array.from(files).some(isFolderTreeFile);
          if (!isFolderPick && !Array.from(files).some(isImportableFile)) {
            toast.error(t.app.toasts.workspaceImportEmpty);
            return;
          }
          void runImport(files);
        }}
        ref={fileInputRef}
        type="file"
      />
      <input
        className="hidden"
        data-testid="settings-import-folder-input"
        multiple
        onChange={(event) => {
          void runImport(event.target.files);
        }}
        ref={(node) => {
          folderInputRef.current = node;
          if (node) {
            node.setAttribute('webkitdirectory', '');
            node.setAttribute('directory', '');
          }
        }}
        type="file"
      />

      <SettingsPage.SettingsSection
        title={t.app.settings.importExport.importSection}
      >
        <SettingsPage.SettingsRow
          control={
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="settings-import-files"
                disabled={!hasWorkspace || busy !== undefined}
                onClick={() => fileInputRef.current?.click()}
                size="sm"
                variant="outline"
              >
                <Upload className="h-4 w-4" />
                {t.app.settings.importExport.importFiles}
              </Button>
              <Button
                data-testid="settings-import-folder"
                disabled={!hasWorkspace || busy !== undefined}
                onClick={() => folderInputRef.current?.click()}
                size="sm"
                variant="outline"
              >
                <FolderOpen className="h-4 w-4" />
                {t.app.settings.importExport.importFolder}
              </Button>
            </div>
          }
          description={t.app.settings.importExport.importDescription}
          title={t.app.settings.importExport.importTitle}
        />
        {importProgress ? (
          <div
            className="space-y-2 border-border/70 border-t px-4 py-4 sm:px-5"
            data-testid="settings-import-progress"
          >
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium">
                {t.app.settings.importExport.importing}
              </span>
              <span className="shrink-0 text-muted-foreground tabular-nums">
                {t.app.settings.importExport.importProgress(importProgress)}
              </span>
            </div>
            <div
              aria-label={
                importProgress.currentFile
                  ? t.app.settings.importExport.importingFile({
                      fileName: importProgress.currentFile,
                    })
                  : t.app.settings.importExport.importing
              }
              aria-valuemax={importProgress.total}
              aria-valuemin={0}
              aria-valuenow={importProgress.completed}
              aria-valuetext={
                importProgress.currentFile
                  ? `${t.app.settings.importExport.importProgress(importProgress)} — ${importProgress.currentFile}`
                  : t.app.settings.importExport.importProgress(importProgress)
              }
              className="h-2 overflow-hidden rounded-full bg-muted"
              role="progressbar"
            >
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-150 ease-out"
                style={{ width: `${importPercent}%` }}
              />
            </div>
            <p
              className="min-h-5 truncate font-mono text-muted-foreground text-xs"
              data-testid="settings-import-progress-file"
              title={importProgress.currentFile}
            >
              {importProgress.currentFile
                ? t.app.settings.importExport.importingFile({
                    fileName: importProgress.currentFile,
                  })
                : '\u00a0'}
            </p>
          </div>
        ) : null}
      </SettingsPage.SettingsSection>

      <SettingsPage.SettingsSection
        title={t.app.settings.importExport.exportSection}
      >
        <SettingsPage.SettingsRow
          control={
            <Button
              data-testid="settings-export-workspace"
              disabled={!hasWorkspace || busy !== undefined}
              onClick={() => void runExport()}
              size="sm"
              variant="outline"
            >
              <Download className="h-4 w-4" />
              {busy === 'export'
                ? t.app.settings.importExport.exporting
                : t.app.settings.importExport.exportWorkspace}
            </Button>
          }
          description={t.app.settings.importExport.exportDescription}
          title={t.app.settings.importExport.exportTitle}
        />
        {exportProgress ? (
          <div
            className="space-y-2 border-border/70 border-t px-4 py-4 sm:px-5"
            data-testid="settings-export-progress"
          >
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium">
                {t.app.settings.importExport.exporting}
              </span>
              <span className="shrink-0 text-muted-foreground tabular-nums">
                {t.app.settings.importExport.exportProgress(exportProgress)}
              </span>
            </div>
            <div
              aria-label={
                exportProgress.currentFile
                  ? t.app.settings.importExport.exportingFile({
                      fileName: exportProgress.currentFile,
                    })
                  : t.app.settings.importExport.exporting
              }
              aria-valuemax={exportProgress.total}
              aria-valuemin={0}
              aria-valuenow={Math.floor(exportProgress.completed)}
              aria-valuetext={
                exportProgress.currentFile
                  ? `${t.app.settings.importExport.exportProgress(exportProgress)} — ${exportProgress.currentFile}`
                  : t.app.settings.importExport.exportProgress(exportProgress)
              }
              className="h-2 overflow-hidden rounded-full bg-muted"
              role="progressbar"
            >
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-150 ease-out"
                style={{ width: `${exportPercent}%` }}
              />
            </div>
            <p
              className="min-h-5 truncate font-mono text-muted-foreground text-xs"
              data-testid="settings-export-progress-file"
              title={exportProgress.currentFile}
            >
              {exportProgress.currentFile
                ? t.app.settings.importExport.exportingFile({
                    fileName: exportProgress.currentFile,
                  })
                : '\u00a0'}
            </p>
          </div>
        ) : null}
      </SettingsPage.SettingsSection>
    </div>
  );
}
